import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useToast } from "./common/Toast";
import { useTx } from "../context/TxContext";
import { useApprove } from "../hooks/useApprove";
import { getPurchaseContract } from "../web3/contracts";
import { parseError } from "../utils/errors";
import { fmtUsdt, parseUsdt } from "../utils/format";
import { DEFAULT_REFERRER } from "../config/app.config";
import { PACKAGES } from "../utils/constants";
import Button from "./common/Button";
import Icon from "./common/Icon";
import Confetti from "./common/Confetti";
import "./Register.css";

const PERKS = [
  { icon: "coins", text: "Receive SCN tokens instantly on activation" },
  { icon: "users", text: "Unlock direct referral income (up to 20%)" },
  { icon: "node", text: "Get placed in the 2×10 matrix network" },
  { icon: "layers", text: "Start earning 10 generation levels deep" },
];

export default function Register({ usdtBal, onDone }) {
  const { signer } = useWeb3();
  const toast = useToast();
  const { run } = useTx();
  const { ensureAllowance } = useApprove();

  const [ref, setRef] = useState("");
  const [burst, setBurst] = useState(0);
  const price = PACKAGES[0].price; // $10

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("ref");
    if (p && /^\d+$/.test(p)) setRef(p);
  }, []);

  const insufficient = usdtBal && usdtBal.lt(parseUsdt(price));

  const onRegister = async () => {
    if (!signer) return;
    const referrerId = ref && /^\d+$/.test(ref) ? ref : String(DEFAULT_REFERRER);
    try {
      await run("Approving USDT", async () => {
        const approved = await ensureAllowance(parseUsdt(price));
        if (approved) toast.success("USDT approved.");
      });
      await run("Activating your node", async () => {
        const purchase = getPurchaseContract(signer);
        const tx = await purchase.register(ethers.BigNumber.from(referrerId));
        await tx.wait();
      });
      setBurst(Date.now());
      toast.success("Welcome aboard! Your node is active.");
      onDone && onDone();
    } catch (e) {
      toast.error(parseError(e));
    }
  };

  return (
    <div className="card reg-card fade-up">
      {burst > 0 && <Confetti key={burst} />}

      <div className="reg-head">
        <span className="reg-ico"><Icon name="rocket" size={22} /></span>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Activate Your First Node</h2>
          <p className="muted" style={{ margin: "4px 0 0", fontSize: 13 }}>
            Join with <strong>Node Starter</strong> for <strong>${price} USDT</strong>.
          </p>
        </div>
      </div>

      <div className="reg-perks">
        {PERKS.map((p, i) => (
          <div className="reg-perk" key={i}>
            <span className="reg-perk-ico"><Icon name={p.icon} size={16} /></span>
            <span>{p.text}</span>
          </div>
        ))}
      </div>

      <label className="field-label mt-4">Referrer ID (optional)</label>
      <input
        className="input"
        placeholder={String(DEFAULT_REFERRER)}
        value={ref}
        onChange={(e) => setRef(e.target.value.replace(/[^\d]/g, ""))}
        inputMode="numeric"
      />
      <p className="muted reg-hint">
        Leave blank to join under the default node. If you used a referral link, this is filled for you.
      </p>

      <div className="reg-balance">
        <span className="muted">Your USDT balance</span>
        <span>{usdtBal ? fmtUsdt(usdtBal) : "0"} USDT</span>
      </div>

      {insufficient && (
        <p className="reg-warn">⚠ You need at least ${price} USDT to activate.</p>
      )}

      <Button className="btn-block mt-3" onClick={onRegister} disabled={insufficient}>
        Register &amp; Activate · ${price}
      </Button>

      <p className="reg-fine muted">
        One USDT approval may be requested first, then your activation transaction. All income and
        SCN are handled by the smart contract on-chain.
      </p>
    </div>
  );
}
